import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import playIcon from "../../images/playIcon.svg";

export const AthleteStrengthWorkoutExercise = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { product_basic, weekNumber, dayName, exercise } = location.state || {};

  const exerciseData = exercise;
  if (!exerciseData) console.warn("No exercise data passed!");

  const [showVideo, setShowVideo] = useState(false);
  const [embedUrl, setEmbedUrl] = useState("");
  const [oneRepMax, setOneRepMax] = useState("");
  const [sets, setSets] = useState([]);
  const [originalSets, setOriginalSets] = useState([]);
  const [openAcc, secOpenAcc] = useState(null);

  const handleOpenAccordion = (index) => {
    secOpenAcc((prev) => (prev === index ? null : index));
  };

  // --------- UTILS ----------
  const extractYouTubeID = (url) => {
    if (!url) return null;
    const regex =
      /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([0-9A-Za-z_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const convertToEmbedUrl = (url) => {
    const id = extractYouTubeID(url);
    return id ? `https://www.youtube.com/embed/${id}` : url;
  };

  const getThumbnail = (url) => {
    const id = extractYouTubeID(url);
    return id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : null;
  };

  // --------- LOAD DATA ----------
  useEffect(() => {
    if (!exerciseData) return;

    const ex = exerciseData.exercise_id || exerciseData;
    setEmbedUrl(convertToEmbedUrl(ex.video_url));

    if (exerciseData.sets) {
      setSets(exerciseData.sets);
      setOriginalSets(exerciseData.sets);
    }

    // Load 1RM from localStorage if available
    const storedOneRm = localStorage.getItem(`oneRepMax-${ex._id}`);
    if (storedOneRm) {
      const val = parseFloat(storedOneRm);
      setOneRepMax(val);
      if (val > 0) updateSetWeights(val, exerciseData.sets);
    }
  }, [exerciseData]);

  // --------- 1RM LOGIC ----------
  const updateSetWeights = (newOneRepMax, baseSets = originalSets) => {
    if (!newOneRepMax || isNaN(newOneRepMax) || newOneRepMax <= 0) return;

    const updatedSets = baseSets.map((set) => {
      if (set.unit === "%") {
        const raw = (set.weight * newOneRepMax) / 100;
        const rounded = Math.round(raw / 5) * 5; // ← ROUND TO NEAREST 5

        return { ...set, weight: rounded, unit: "lbs" };
      }
      // If unit is "hide", leave weight and unit as is
      return set;
    });

    setSets(updatedSets);
  };

  const handleOneRepMaxChange = (e) => {
    const val = parseFloat(e.target.value);
    const ex = exerciseData?.exercise_id || exerciseData;

    if (!isNaN(val) && val > 0) {
      setOneRepMax(val);
      localStorage.setItem(`oneRepMax-${ex._id}`, val);
      updateSetWeights(val);
    } else {
      setOneRepMax("");
      setSets(originalSets); // Restore original sets
    }
  };

  const handleBackClick = () => navigate(-1);

  const [showFullDesc, setShowFullDesc] = useState(false);

  const renderExerciseDetails = (ex) => {
    const obj = ex.exercise_id || ex;
    const thumbnail = getThumbnail(obj.video_url);

    return (
      <div key={obj._id} className="py-4">
        <div className="relative h-[210px] mb-4">
          <img
            src={
              thumbnail ||
              "https://via.placeholder.com/640x360?text=No+Thumbnail"
            }
            className="h-full w-full object-cover"
            alt={obj.title}
          />
          {obj.video_url && (
            <img
              src={playIcon}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
              onClick={() => {
                setShowVideo(true);
                setEmbedUrl(convertToEmbedUrl(obj.video_url));
              }}
              alt="Play"
            />
          )}
          <div className="absolute bottom-4 left-4">
            <p className="text-white text-lg xs:text-xl font-light uppercase">
              {obj.title}
            </p>
          </div>
        </div>
        <p className="text-black font-medium mt-2 whitespace-pre-line">
          {showFullDesc ? obj.description : obj.description?.slice(0, 180)}
        </p>
        {obj.description?.length > 180 && (
          <button
            className="text-blue-400 underline font-medium mt-2 text-sm"
            onClick={() => setShowFullDesc(!showFullDesc)}
          >
            {showFullDesc ? "Show Less" : "Show More"}
          </button>
        )}

        {/* --- 1RM INPUT (UPDATED UX) --- */}
        <div className="w-full mt-3">
          <div className="w-full relative">
            <label className="text-grayish font-light text-sm pr-3 border-r border-grayish absolute top-3 left-3">
              Enter 1RM
            </label>
            <input
              type="number"
              value={oneRepMax}
              onChange={handleOneRepMaxChange}
              // placeholder="ENTER (1RM) TO UPDATE % SETS"
              className=" w-full border border-grayish rounded-2xl bg-transparent text-black font-light text-base leading-none py-3 pl-28 placeholder:text-grayish placeholder:uppercase "
            />
          </div>
        </div>
      </div>
    );
  };

  if (!exerciseData)
    return (
      <AthleteLayout>
        <p className="text-black text-center py-10">No data</p>
      </AthleteLayout>
    );

  const ex = exerciseData.exercise_id || exerciseData;

  return (
    <AthleteLayout>
      <div className="h-full overflow-y-auto bg-white">
        {/* PRODUCT HEADER */}
        <div className="w-full max-w-2xl mx-auto px-3 xs:px-5">
          <div className=" pt-3 bg-white ">
            <div className="relative py-4 text-center border-b border-black/30 mb-4">
              <span
                className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-2 cursor-pointer text-black"
                onClick={handleBackClick}
              >
                <BackArrow />
              </span>
              <p className="text-black text-lg font-bold">
                {product_basic?.title || "Workout"}
              </p>
              {/* <p className="text-gray-600 text-sm mt-1">
              Week {weekNumber || "-"} • {dayName || "-"}
            </p> */}
            </div>
            <div className="bg-black py-3 px-4 rounded-2xl mb-2 flex items-center gap-2">
              <p className="text-white font-light text-lg xs:text-xl !leading-none">
                W{weekNumber || "-"}
              </p>
              <p className="text-white font-light text-lg xs:text-xl">|</p>
              <p className="text-white font-light text-lg xs:text-xl !leading-none capitalize">
                {dayName || "-"}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full max-w-2xl mx-auto px-3 xs:px-5">
          {/* Exercise Info */}
          {renderExerciseDetails(ex)}

          {/* Outer container for all sets with horizontal padding */}
          <div className="w-full pb-7">
            <div className="mb-5">
              <div className="bg-black rounded-xl py-3 px-3 divide-x-2 divide-white/50 flex flex-row justify-between items-stretch text-center relative z-10">
                <div className="w-[15%] px-1.5 flex justify-center items-center">
                  <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                    Sets
                  </p>
                </div>
                <div className="w-[15%] px-1.5 flex justify-center items-center">
                  <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                    Reps
                  </p>
                </div>
                <div className="w-[28%] px-1 flex justify-center items-center">
                  <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase hidden xs:block">
                    Load/Time
                  </p>
                  <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase block xs:hidden">
                    Load/Time
                  </p>
                </div>
                <div className="w-[28%] px-1 flex justify-center items-center">
                  <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase min-w-14 xs:min-w-[76px]">
                    goal
                  </p>
                </div>
                <div className="w-[10%] px-1 flex justify-center items-center">
                  <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                    Rest
                  </p>
                </div>
                <div className="w-[6%] opacity-0 xs:mr-1.5">
                  <div className="w-5 h-5 xs:w-6 xs:h-6"></div>
                </div>
              </div>
            </div>

            {sets.map((s, i) => {
              const oneRmRounded = s.oneRM ? Math.round(s.oneRM / 5) * 5 : null;

              return (
                <div
                  key={s._id || i}
                  onClick={() => handleOpenAccordion(i)}
                  className="mb-5 p-3 xs:p-5 relative border border-black/30 rounded-2xl"
                >
                  {/* Card */}
                  <div className="relative flex flex-wrap items-stretch cursor-pointer">
                    {/* Left column */}
                    {/* <div className="max-xs:h-auto flex-1 flex justify-center items-center border-r border-black">
                      <p className="text-black max-xs:text-[11px] font-light text-center px-1">
                        {i + 1}
                      </p>
                    </div> */}
                    <div className="max-xs:h-auto w-[14%] flex justify-center items-center border-r border-black">
                      <p className="text-black max-xs:text-[11px] font-light text-center px-1">
                        1
                      </p>
                    </div>

                    {/* Right column */}
                    {/* First row: Reps × Weight left, Goal right */}
                    <div className="max-xs:h-auto w-[15%] flex justify-center items-center border-r border-black">
                      {s.unit !== "hide" && s.reps && (
                        <p className="text-black max-xs:text-[11px] font-light text-center truncate px-1">
                          {s.reps}
                        </p>
                      )}
                    </div>
                    <div className="max-xs:h-auto w-[28%] flex justify-center items-center border-r border-black">
                      {s.unit !== "hide" && s.reps && (
                        <p className="text-black max-xs:text-[11px] font-light text-center truncate px-1">
                          {s.unit === "time"
                            ? `${s.time || ""} sec`
                            : `${s.weight || ""} ${s.unit}`}
                        </p>
                      )}
                    </div>
                    <div className="max-xs:h-auto w-[28%] flex justify-center items-center gap-1 border-r border-black">
                      {s.goal && (
                        <p className="text-black max-xs:text-[11px] font-light text-center truncate px-1">
                          {/* {s.goal} {s.goalType || ""} */}
                          {s.goal.replace(/\s*\([^)]*\)/g, "")}{" "}
                          {s.goalType || ""}
                        </p>
                      )}
                      {oneRmRounded && (
                        <p className="text-black max-xs:text-[11px] font-light text-center sm:ml-2 px-1">
                          1RM: {oneRmRounded}
                        </p>
                      )}
                    </div>

                    {/* Second row: Rest aligned under left content */}
                    <div className="max-xs:h-auto w-[10%]">
                      {s.rest && (
                      <div className="flex justify-center items-center">
                        {(() => {
                          const minutes = s.rest.match(/\d+/)?.[0]; // extract number

                          return (
                            <p className="text-black max-xs:text-[11px] font-light text-center px-1">
                              {minutes}m
                            </p>
                          );
                        })()}
                      </div>
                       )}
                    </div>
                    <div className="w-[5%]">
                      <span
                        className={`transform transition-transform duration-300 cursor-pointer flex justify-center items-center bg-darkYellow p-1 text-black leading-none w-5 h-5 xs:w-6 xs:h-6 text-center`}
                      >
                        {/* <Plus /> */}
                        {openAcc === i ? <Minus /> : <Plus />}
                      </span>
                    </div>
                  </div>

                  {/* Notes outside rectangle, aligned under divider */}
                  {openAcc === i && s.notes && (
                    <div className="pt-3 mt-3 border-t border-darkYellow">
                      <p className="text-black font-light truncate">
                        Notes: {s.notes}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>{" "}
      {/* ← close scroll container */}
      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-3 z-50">
          <div className="relative w-[90%] max-w-lg bg-black">
            <button
              className="absolute top-2 right-2 bg-gray-400 rounded-md p-2 text-white hover:bg-gray-300 transition"
              onClick={() => setShowVideo(false)}
            >
              Close
            </button>
            <iframe
              width="100%"
              height="315"
              src={embedUrl}
              title="Workout Video"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </AthleteLayout>
  );
};

const BackArrow = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

const Plus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      <path d="M11 4.5H13V19.5H11V4.5Z" fill="black" />
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};

const Minus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      {/* <path d="M10.5 4H13.5V20H10.5V4Z" fill="black" /> */}
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};
